import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { rememberUser } from "../hooks/useUserState";
import { RelayLoader } from "../components/RelayLoader";
import { GoogleConsentHint } from "../components/GoogleConsentHint";
import { SiteFooter } from "../components/SiteFooter";

const API = process.env.REACT_APP_BACKEND_URL;
const serif = { fontFamily: "'Fraunces', Georgia, serif" };


const COLOR = {
  bg: "#FAF7F0",
  ink: "#2A1E5C",
  ink2: "#5D5A6B",
  muted: "#8B7E6A",
  line: "#E8E2D5",
  card: "#FFFFFF",
  gold: "#F5CB5C",
  red: "#DE3C4B",
  turquoise: "#2E86AB",
  teal: "#114B5F",
  green: "#3E8E4E",
  greenLine: "#B7D9B0",
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtMoney = (n) =>
  n == null || n === "" ? "n/a" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const BUILD_MESSAGES = [
  "Copying the master template into your Drive…",
  "Copying the LEGEND + SCOREBOARD tabs…",
  "Copying all 12 monthly relay tabs…",
  "Pre-loading your Relay Calculator numbers…",
  "Setting your relay calendar…",
];

export default function SheetSetupPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [building, setBuilding] = useState(false);
  const [buildMsgIdx, setBuildMsgIdx] = useState(0);
  const [sheetUrl, setSheetUrl] = useState(null);
  const [editSettings, setEditSettings] = useState(false);
  const [showTrust, setShowTrust] = useState(false);
  const [scopeBlocked, setScopeBlocked] = useState(false);
  const [editIncomeMap, setEditIncomeMap] = useState(false);
  const [activeToken, setActiveToken] = useState("");
  const tok = activeToken || token;
  const [showPact, setShowPact] = useState(false);
  const [pitStop, setPitStop] = useState(null);

  const [preferredName, setPreferredName] = useState("");
  const [paydayDelay, setPaydayDelay] = useState(3);
  const [startDate, setStartDate] = useState(todayIso());
  const [weeklyTarget, setWeeklyTarget] = useState("");
  const [daysPerWeek, setDaysPerWeek] = useState("");
  const [workingWeeks, setWorkingWeeks] = useState("");

  const loadInfo = useCallback(async () => {
    try {
      // No token, or a token from another environment: the backend falls back to the
      // signed-in racer's own setup, so nobody gets stranded on "invalid link".
      const res = await fetch(
        `${API}/api/sheet-setup/info${token ? `?token=${token}` : ""}`,
        { credentials: "include" }
      );
      if (!res.ok) {
        setNotFound(true);
        return;
      }
      const data = await res.json();
      setInfo(data);
      if (data.setup_token && data.setup_token !== token) setActiveToken(data.setup_token);
      if (data.email) rememberUser(data.email);
      setSheetUrl(data.sheet_url || null);
      if (data.payday_delay != null) setPaydayDelay(data.payday_delay);
      if (data.start_date) setStartDate(data.start_date);
      if (data.preferred_name) setPreferredName(data.preferred_name);
      if (data.prefill) {
        if (data.prefill.weekly_target != null) setWeeklyTarget(String(data.prefill.weekly_target));
        if (data.prefill.days_per_week != null) setDaysPerWeek(String(data.prefill.days_per_week));
        if (data.prefill.working_weeks != null) setWorkingWeeks(String(data.prefill.working_weeks));
      }
    } catch {
      toast.error("Couldn't load your setup. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  useEffect(() => {
    const g = searchParams.get("google");
    if (!g) return;
    if (g === "connected") toast.success("Google account connected!");
    if (g === "denied") toast.error("Google connection was cancelled. Payday Relay runs on Google Sheets, try connecting again when you're ready.");
    if (g === "error") toast.error("Something went wrong connecting Google. Please try again.");
    if (g === "scopes") {
      setScopeBlocked(true);
      toast.error("Google didn't grant spreadsheet access.", { duration: 12000 });
    }
    const next = new URLSearchParams(searchParams);
    next.delete("google");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!building) return;
    const id = setInterval(
      () => setBuildMsgIdx((i) => Math.min(i + 1, BUILD_MESSAGES.length - 1)),
      4500,
    );
    return () => clearInterval(id);
  }, [building]);

  // Load Pit Stop status for the Commitment Pact modal, shows the exact next window.
  useEffect(() => {
    fetch(`${API}/api/caddy/pit-stop-status`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setPitStop)
      .catch(() => {});
  }, []);

  const isFourRelay = info?.product_variant === "four_relay";
  const relayCount = isFourRelay ? "4-relay" : "52-relay";

  const hasIncomeMap = useMemo(
    () => !!(info?.prefill && info.prefill.weekly_target != null),
    [info],
  );
  const connected = info?.google_connected;
  const needsReconnect = !!info?.needs_reconnect;
  const googleReady = connected && !needsReconnect;
  const showSuccess = sheetUrl && !editSettings;
  const fromApp = searchParams.get("from") === "app";

  const buildBody = () => {
    const body = { token: tok, payday_delay: Number(paydayDelay), start_date: startDate };
    if (preferredName.trim()) body.preferred_name = preferredName.trim();
    if (weeklyTarget !== "") body.weekly_target = Number(weeklyTarget);
    if (daysPerWeek !== "") body.days_per_week = Number(daysPerWeek);
    if (workingWeeks !== "") body.working_weeks = Number(workingWeeks);
    return body;
  };

  const connectGoogle = () => {
    window.location.href = `${API}/api/oauth/sheets/login?token=${tok}`;
  };

  const buildSheet = async () => {
    setBuilding(true);
    setBuildMsgIdx(0);
    try {
      const res = await fetch(`${API}/api/sheet-setup/build`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildBody()),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Build failed, please try again.");
        return;
      }
      setSheetUrl(data.sheet_url);
      setEditSettings(false);
      toast.success("Your Payday Relay sheet is ready!");
    } catch {
      toast.error("Network hiccup, please try again.");
    } finally {
      setBuilding(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: COLOR.bg }}>
      <header className="py-2 px-6 border-b" style={{ borderColor: COLOR.line }}>
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center"
            data-testid="setup-home-link"
          >
          </Link>
          <span className="text-xs uppercase tracking-widest" style={{ color: COLOR.muted }}>
            Sheet Setup
          </span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10">
        {loading ? (
          <div className="py-20" data-testid="setup-loading">
            <RelayLoader label="Loading your setup…" />
          </div>
        ) : notFound ? (
          <InvalidLink />
        ) : showSuccess ? (
          <SuccessView
            sheetUrl={sheetUrl}
            token={tok}
            isFourRelay={isFourRelay}
            relayCount={relayCount}
            onEdit={() => setEditSettings(true)}
          />
        ) : !hasIncomeMap ? (
          <MissingIncomeMap email={info?.email} />
        ) : (
          <div data-testid="setup-wizard">
            {fromApp && (
              <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: COLOR.gold }} data-testid="setup-step-kicker">
                Step 2 of 3 · Sync your sheet
              </p>
            )}
            <h1
              className="text-3xl sm:text-4xl mb-2"
              style={{ ...serif, color: COLOR.ink }}
              data-testid="wizard-title"
            >
              Let's build your {relayCount} sheet
            </h1>
            <p className="text-sm mb-6" style={{ color: COLOR.ink2 }}>
              Two minutes, three steps. Your <b>Google Sheet</b> lands in <b>your own Google Drive</b>,
              pre-loaded with your Relay Calculator numbers.
            </p>

            {/* Meet Your Legend intro */}
            <LegendIntroCard isFourRelay={isFourRelay} />

            {/* Step 1: Connect Google */}
            <section
              className="rounded-2xl p-6 mb-5 border"
              style={{ background: COLOR.card, borderColor: googleReady ? COLOR.greenLine : COLOR.line }}
              data-testid="step-connect-google"
            >
              <div className="flex items-start gap-4">
                <StepBadge n="1" done={googleReady} />
                <div className="flex-1">
                  <h2 className="font-bold mb-1" style={{ color: COLOR.ink }}>
                    Connect your Google account
                  </h2>
                  {scopeBlocked && (
                    <div
                      className="rounded-xl p-3 mb-3 border text-sm"
                      style={{ borderColor: COLOR.red, background: "rgba(198,40,40,0.06)", color: COLOR.ink }}
                      data-testid="google-scope-blocked"
                    >
                      <b>One box was left unticked.</b> On the Google screen, keep{" "}
                      <b>“See, edit, create and delete all your Google Sheets spreadsheets”</b> switched
                      on. That is the permission that lets us build your sheet. Nothing else is
                      touched. Press Connect Google again and tick it.
                    </div>
                  )}
                  {connected && !needsReconnect ? (
                    <>
                      <p className="text-sm" style={{ color: COLOR.green }} data-testid="google-connected-label">
                        Connected{info?.google_email ? ` as ${info.google_email}` : ""} ✓
                      </p>
                      <p className="text-xs mt-1.5 leading-relaxed" style={{ color: COLOR.muted }}>
                        That's where your sheet gets built. It needn't match your sign-in email.{" "}
                        {info?.google_configured && (
                          <button
                            onClick={() => setShowTrust(true)}
                            className="underline font-semibold"
                            style={{ color: COLOR.ink }}
                            data-testid="switch-google-account-btn"
                          >
                            Use a different Google account
                          </button>
                        )}
                      </p>
                    </>
                  ) : connected && needsReconnect ? (
                    <>
                      <p className="text-sm font-semibold" style={{ color: COLOR.ink }} data-testid="google-reconnect-label">
                        Your Google access has expired
                      </p>
                      <p className="text-sm mt-1 mb-3 leading-relaxed" style={{ color: COLOR.ink2 }}>
                        {info?.google_email ? <>Google ended the permission for <b>{info.google_email}</b>. </> : null}
                        Reconnect once and you can build your sheet. Nothing you entered is lost.
                      </p>
                      {info?.google_configured && (
                        <button
                          onClick={() => setShowTrust(true)}
                          className="px-6 py-3 rounded-full font-semibold text-sm transition-transform hover:scale-105"
                          style={{ background: COLOR.ink, color: "#fff" }}
                          data-testid="reconnect-google-btn"
                        >
                          Reconnect Google →
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-sm mb-2" style={{ color: COLOR.ink2 }}>
                        Payday Relay runs on <b>Google Sheets</b> so your formulas, formatting, and
                        Payday Relay sync stay consistent. Your sheet gets copied into <b>your</b> Drive
                        so you own it forever. We can only touch spreadsheets. Never your files,
                        photos, or email.
                      </p>
                      <p className="text-xs mb-4" style={{ color: COLOR.muted }}>
                        You'll need a Google account to create your sheet in Google Drive, but
                        <b> you do not need a Gmail address</b>.
                      </p>
                      {info?.google_configured ? (
                        <>
                          <GoogleConsentHint />
                          <button
                            onClick={() => setShowTrust(true)}
                            className="px-6 py-3 rounded-full font-semibold text-sm transition-transform hover:scale-105"
                            style={{ background: COLOR.ink, color: "#fff" }}
                            data-testid="connect-google-btn"
                          >
                            Connect Google →
                          </button>
                          <p className="mt-3 text-xs" style={{ color: COLOR.ink2 }}>
                            Don't have a Google account?{" "}
                            <a
                              href="https://accounts.google.com/signup"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline font-semibold"
                              style={{ color: COLOR.ink }}
                              data-testid="create-google-account-link"
                            >
                              You can create one with any email address
                            </a>
                            {" "}, Gmail is not required.
                          </p>
                        </>
                      ) : (
                        <p
                          className="text-sm italic px-4 py-3 rounded-lg"
                          style={{ background: COLOR.bg, color: COLOR.muted }}
                          data-testid="google-not-configured"
                        >
                          Google connection is being configured, check back shortly, or reply to
                          your purchase email and we'll get you set up.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </section>

            {/* Step 2: Payday Delay + Start Date + Income Map Summary */}
            <section
              className="rounded-2xl p-6 mb-5 border"
              style={{
                background: COLOR.card,
                borderColor: COLOR.line,
                opacity: googleReady ? 1 : 0.55,
                pointerEvents: googleReady ? "auto" : "none",
              }}
              data-testid="step-calendar"
            >
              <div className="flex items-start gap-4">
                <StepBadge n="2" done={false} />
                <div className="flex-1 space-y-6">
                  <div>
                    <h2 className="font-bold" style={{ color: COLOR.ink }}>
                      Set your relay calendar
                    </h2>
                    <p className="text-xs mt-1" style={{ color: COLOR.muted }}>
                      Three quick answers. Everything else comes from your Relay Calculator.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1" style={{ color: COLOR.ink }}>
                      What should we call you?
                    </label>
                    <p className="text-xs mb-2" style={{ color: COLOR.muted }}>
                      First name or nickname. It&apos;s how your dashboard greets you.
                    </p>
                    <input
                      type="text"
                      maxLength={40}
                      value={preferredName}
                      onChange={(e) => setPreferredName(e.target.value)}
                      placeholder="e.g. Dove"
                      className="w-full max-w-xs px-3 py-2 rounded-lg border text-sm"
                      style={{ borderColor: COLOR.line, color: COLOR.ink }}
                      data-testid="preferred-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1" style={{ color: COLOR.ink }}>
                      Payday rhythm (days)
                    </label>
                    <p className="text-xs mb-2" style={{ color: COLOR.muted }}>
                      After a 7-day relay ends, how many days until the money hits your account?
                      <br />
                      <span className="italic">
                        Pick a delay that matches how money reaches you, or the rhythm you want to create.
                      </span>
                    </p>
                    <div className="flex gap-2 flex-wrap items-center">
                      {[0, 3, 7, 14].map((d) => (
                        <button
                          key={d}
                          onClick={() => setPaydayDelay(d)}
                          className="px-4 py-2 rounded-full text-sm font-semibold border transition-colors"
                          style={{
                            background: Number(paydayDelay) === d ? COLOR.ink : "#fff",
                            color: Number(paydayDelay) === d ? "#fff" : COLOR.ink2,
                            borderColor: COLOR.line,
                          }}
                          data-testid={`payday-delay-${d}`}
                        >
                          {d} days
                        </button>
                      ))}
                      <input
                        type="number"
                        min="0"
                        max="60"
                        value={paydayDelay}
                        onChange={(e) => setPaydayDelay(e.target.value)}
                        className="w-20 px-3 py-2 rounded-lg border text-sm"
                        style={{ borderColor: COLOR.line, color: COLOR.ink }}
                        data-testid="payday-delay-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1" style={{ color: COLOR.ink }}>
                      First pay period start date
                    </label>
                    <p className="text-xs mb-2" style={{ color: COLOR.muted }}>
                      The day your first 7-day relay begins. Most people pick the coming Monday.
                    </p>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="px-3 py-2 rounded-lg border text-sm"
                      style={{ borderColor: COLOR.line, color: COLOR.ink }}
                      data-testid="start-date-input"
                    />
                  </div>

                  <GoldenRuleCallout />

                  {/* Relay Calculator summary card, locked by default */}
                  <IncomeMapSummary
                    prefill={info.prefill}
                    editing={editIncomeMap}
                    onEditToggle={() => setEditIncomeMap((v) => !v)}
                    weeklyTarget={weeklyTarget}
                    daysPerWeek={daysPerWeek}
                    workingWeeks={workingWeeks}
                    setWeeklyTarget={setWeeklyTarget}
                    setDaysPerWeek={setDaysPerWeek}
                    setWorkingWeeks={setWorkingWeeks}
                  />
                </div>
              </div>
            </section>

            {/* Step 3: Build */}
            <section
              className="rounded-2xl p-6 border text-center"
              style={{
                background: COLOR.ink,
                borderColor: COLOR.ink,
                opacity: googleReady ? 1 : 0.55,
                pointerEvents: googleReady ? "auto" : "none",
              }}
              data-testid="step-build"
            >
              {building ? (
                <div className="py-2" data-testid="build-progress">
                  <RelayLoader
                    tone="dark"
                    size="md"
                    label={BUILD_MESSAGES[buildMsgIdx]}
                    subLabel="This takes about 20–30 seconds, don't close the page."
                  />
                </div>
              ) : (
                <>
                  <button
                    onClick={() => {
                      // On FIRST build, show the Commitment Pact first.
                      // On re-edit / update-only, skip the pact.
                      if (!sheetUrl) setShowPact(true);
                      else buildSheet();
                    }}
                    disabled={!googleReady || !startDate}
                    className="px-8 py-4 rounded-full font-bold text-base transition-transform hover:scale-105 disabled:opacity-50"
                    style={{ background: COLOR.gold, color: COLOR.ink }}
                    data-testid="build-sheet-btn"
                  >
                    {sheetUrl ? "Update My Sheet Settings" : "Build My Sheet in My Drive 🏁"}
                  </button>
                  <p className="text-xs mt-3" style={{ color: "#B8B3CE" }}>
                    {sheetUrl
                      ? "Updates the LEGEND tab of your existing sheet."
                      : "Copies every tab of the master template, colors, formulas and all."}
                  </p>
                  {sheetUrl && (
                    <button
                      onClick={() => setEditSettings(false)}
                      className="text-xs underline mt-2"
                      style={{ color: "#B8B3CE" }}
                      data-testid="cancel-edit-btn"
                    >
                      Never mind, back to my sheet
                    </button>
                  )}
                </>
              )}
            </section>

            <p className="text-center text-xs mt-6" style={{ color: COLOR.muted }}>
              Setting up for {info?.email}
            </p>
            <p className="text-center text-xs mt-3" style={{ color: COLOR.muted }}>
              Stuck? <a href="/support" target="_blank" rel="noreferrer" className="underline" style={{ color: COLOR.ink2 }} data-testid="setup-coach-link">Ask Caddy 🏁</a>
            </p>
          </div>
        )}
      </main>

      {showTrust && (
        <TrustModal
          onConfirm={() => {
            setShowTrust(false);
            connectGoogle();
          }}
          onCancel={() => setShowTrust(false)}
        />
      )}
      {showPact && (
        <CommitmentPactModal
          pitStop={pitStop}
          goalStatement={info?.prefill?.goal_statement}
          onAgree={() => {
            setShowPact(false);
            buildSheet();
          }}
          onCancel={() => setShowPact(false)}
        />
      )}
      <SiteFooter />
    </div>
  );
}

/* ------------------------ Sub-components ------------------------ */

function StepBadge({ n, done }) {
  return (
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0"
      style={{ background: done ? COLOR.green : COLOR.ink, color: "#fff" }}
    >
      {done ? "✓" : n}
    </div>
  );
}

function InvalidLink() {
  return (
    <div className="text-center py-16" data-testid="setup-invalid-token">
      <h1 className="text-3xl mb-4" style={{ ...serif, color: COLOR.ink }}>
        Hmm, that link isn't right
      </h1>
      <p className="text-sm mb-6 max-w-md mx-auto leading-relaxed" style={{ color: COLOR.ink2 }}>
        Sign in and this page will find your setup on its own, no link needed. If you are already
        signed in, your account has not been set up for a sheet yet.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/login?next=/sheet-setup"
          className="inline-block px-6 py-3 rounded-full font-semibold text-sm"
          style={{ background: COLOR.gold, color: COLOR.ink }}
          data-testid="setup-signin-link"
        >
          Sign in to continue
        </Link>
        <a
          href="mailto:support@paydayrelay.com"
          className="inline-block px-6 py-3 rounded-full font-semibold text-sm border"
          style={{ borderColor: COLOR.line, color: COLOR.ink }}
          data-testid="setup-contact-link"
        >
          Email support
        </a>
      </div>
    </div>
  );
}

function MissingIncomeMap({ email }) {
  return (
    <div className="text-center py-12" data-testid="setup-missing-income-map">
      <h1 className="text-3xl mb-3" style={{ ...serif, color: COLOR.ink }}>
        First, let's calculate your relay target
      </h1>
      <p className="text-sm mb-8 leading-relaxed max-w-md mx-auto" style={{ color: COLOR.ink2 }}>
        We couldn't find Relay Calculator results for <b>{email}</b>. Your Payday Relay sheet is built
        from those numbers, so let's grab them first, then come right back to this page.
      </p>
      <Link
        to="/app/calculator?onboarding=1"
        className="inline-block px-8 py-4 rounded-full font-bold text-base transition-transform hover:scale-105"
        style={{ background: COLOR.gold, color: COLOR.ink }}
        data-testid="go-to-income-map"
      >
        Take me to the Relay Calculator →
      </Link>
      <p className="text-xs mt-6" style={{ color: COLOR.muted }}>
        It lives inside the app, sign in as {email} if asked. Lock in your numbers there and you'll land
        right back on this step.
      </p>
    </div>
  );
}

function GoldenRuleCallout() {
  return (
    <div
      className="rounded-xl px-4 py-3 border-l-4"
      style={{ background: "#FFF8E1", borderColor: COLOR.gold }}
      data-testid="golden-rule"
    >
      <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: COLOR.ink }}>
        The Golden Rule
      </p>
      <p className="text-xs" style={{ color: COLOR.ink2 }}>
        You can adjust your targets every quarter, but{" "}
        <b>don't change your start date or payday rhythm</b> once you begin. Commit once. Then run
        the relay.
      </p>
    </div>
  );
}

function LegendIntroCard({ isFourRelay }) {
  return (
    <section
      className="rounded-2xl p-6 mb-5 border"
      style={{ background: "#FFF8E1", borderColor: "#EED89A" }}
      data-testid="legend-intro"
    >
      <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: COLOR.muted }}>
        Ready...
      </p>
      <h2 className="text-xl mb-2" style={{ ...serif, color: COLOR.ink }}>
        Meet Your Legend, the system's command center
      </h2>
      <p className="text-sm leading-relaxed" style={{ color: COLOR.ink2 }}>
        Before you log your first day's earnings, you'll set your rhythm. The LEGEND tab is where
        your Relay Calculator numbers become {isFourRelay ? "your first 4 relays" : "your 52-relay year"}.
        Once it's set, the rest of the sheet builds itself, tracking your taxes, PTO, debt payoff,
        and take-home pay automatically.
      </p>
      <p className="text-xs italic mt-2" style={{ color: COLOR.muted }}>
        Lock it in once and let the year run.
      </p>
    </section>
  );
}

function IncomeMapSummary({
  prefill,
  editing,
  onEditToggle,
  weeklyTarget,
  daysPerWeek,
  workingWeeks,
  setWeeklyTarget,
  setDaysPerWeek,
  setWorkingWeeks,
}) {
  const rows = [
    { label: "Weekly target", value: fmtMoney(prefill.weekly_target) },
    { label: "Work days/week", value: prefill.days_per_week ?? "n/a" },
    { label: "Working weeks/year", value: prefill.working_weeks ?? "n/a" },
    prefill.federal_tax_rate != null && { label: "Federal tax rate", value: `${prefill.federal_tax_rate}%` },
    prefill.debt_amount && { label: "Debt payoff goal", value: fmtMoney(prefill.debt_amount) },
    prefill.savings_goal && { label: "Savings goal", value: fmtMoney(prefill.savings_goal) },
  ].filter(Boolean);

  return (
    <div className="rounded-xl p-4" style={{ background: COLOR.bg }} data-testid="income-map-summary">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-wide" style={{ color: COLOR.muted }}>
          From your Relay Calculator
        </p>
        <button
          onClick={onEditToggle}
          className="text-xs underline"
          style={{ color: COLOR.ink2 }}
          data-testid="income-map-edit-toggle"
        >
          {editing ? "Done editing" : "Edit"}
        </button>
      </div>

      {!editing ? (
        <>
          <ul className="space-y-1.5">
            {rows.map((r) => (
              <li key={r.label} className="flex justify-between text-sm">
                <span style={{ color: COLOR.ink2 }}>{r.label}</span>
                <span className="font-semibold" style={{ color: COLOR.ink }}>
                  {r.value}
                </span>
              </li>
            ))}
          </ul>
          {prefill.goal_statement && (
            <p
              className="text-xs mt-3 italic pt-3 border-t"
              style={{ color: COLOR.muted, borderColor: COLOR.line }}
              data-testid="goal-statement-preview"
            >
              Your why: "{prefill.goal_statement}"
            </p>
          )}
        </>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          <SmallInput
            label="Weekly target ($)"
            value={weeklyTarget}
            onChange={setWeeklyTarget}
            testId="weekly-target-input"
            placeholder="1500"
          />
          <SmallInput
            label="Work days/week"
            value={daysPerWeek}
            onChange={setDaysPerWeek}
            testId="days-per-week-input"
            placeholder="5"
            min={1}
            max={7}
          />
          <SmallInput
            label="Weeks working/yr"
            value={workingWeeks}
            onChange={setWorkingWeeks}
            testId="working-weeks-input"
            placeholder="48"
            min={1}
            max={52}
          />
        </div>
      )}
    </div>
  );
}

function SmallInput({ label, value, onChange, testId, placeholder, min = 0, max }) {
  return (
    <div>
      <label className="block text-xs mb-1" style={{ color: COLOR.ink2 }}>
        {label}
      </label>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-2 py-2 rounded-lg border text-sm"
        style={{ borderColor: COLOR.line, color: COLOR.ink }}
        data-testid={testId}
      />
    </div>
  );
}

function TrustModal({ onConfirm, onCancel }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(42, 30, 92, 0.55)" }}
      data-testid="trust-modal"
      onClick={onCancel}
    >
      <div
        className="max-w-md w-full rounded-2xl p-7"
        style={{ background: COLOR.card }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-4">
          <h3 className="text-xl mb-1" style={{ ...serif, color: COLOR.ink }}>
            You're about to connect to Google
          </h3>
        </div>
        <div
          className="text-sm space-y-3 mb-5 leading-relaxed"
          style={{ color: COLOR.ink2 }}
        >
          <p>
            This is <b>Payday Relay by Shari Dove Shepard</b>. Google will ask you to allow us to create
            and edit spreadsheets, that's it. We can only touch spreadsheets we make for you.
          </p>
          <p style={{ color: COLOR.muted, fontSize: "12px" }}>
            You'll see a Google permissions screen, click "Allow".
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-5 py-3 rounded-full font-semibold text-sm border"
            style={{ borderColor: COLOR.line, color: COLOR.ink2, background: "#fff" }}
            data-testid="trust-modal-cancel"
          >
            Not yet
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-5 py-3 rounded-full font-semibold text-sm transition-transform hover:scale-105"
            style={{ background: COLOR.ink, color: "#fff" }}
            data-testid="trust-modal-confirm"
          >
            Continue to Google →
          </button>
        </div>
      </div>
    </div>
  );
}

function CommitmentPactModal({ pitStop, goalStatement, onAgree, onCancel }) {
  // Show the exact date the next Pit Stop opens, if we're currently in a window,
  // show that window's close date so the language reads correctly.
  const nextDateISO = pitStop?.is_open
    ? pitStop?.current_window?.closes_on
    : pitStop?.next_window?.opens_on;
  const nextDateLabel = nextDateISO
    ? new Date(nextDateISO + "T12:00:00").toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "the next quarterly tax day";
  const nextQuarter = pitStop?.is_open ? pitStop?.current_window?.quarter : pitStop?.next_window?.quarter;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 overflow-y-auto py-8"
      style={{ background: "rgba(42, 30, 92, 0.72)" }}
      data-testid="commitment-pact-modal"
      onClick={onCancel}
    >
      <div
        className="max-w-md w-full rounded-2xl p-7 my-auto"
        style={{ background: COLOR.card }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-5">
          <span className="text-4xl" role="img" aria-label="checkered flag">🏁</span>
          <p
            className="text-[11px] tracking-[0.15em] mt-2 mb-1"
            style={{ color: COLOR.muted }}
          >
            YOUR RELAY AGREEMENT
          </p>
          <h3
            className="text-2xl leading-tight"
            style={{ ...serif, color: COLOR.ink }}
            data-testid="pact-heading"
          >
            Let&apos;s make this real 🎯
          </h3>
        </div>

        {goalStatement && (
          <div
            className="rounded-xl px-4 py-4 mb-4 text-center"
            style={{ background: "#FFF8E4", border: `1px solid ${COLOR.gold}` }}
            data-testid="pact-goal-statement"
          >
            <p className="text-[10px] tracking-[0.15em] mb-2" style={{ color: "#7A5E10" }}>
              YOUR GOAL STATEMENT, FROM YOUR INCOME MAP
            </p>
            <p
              className="text-base leading-snug italic"
              style={{ ...serif, color: COLOR.ink }}
            >
              &ldquo;{goalStatement}&rdquo;
            </p>
          </div>
        )}

        <div
          className="text-sm space-y-3 mb-5 leading-relaxed"
          style={{ color: COLOR.ink2 }}
        >
          <p>
            <b>This is what I&apos;m running toward.</b> Financial clarity comes from consistency,
            not perfection, and I&apos;m ready for both.
          </p>
          <p>
            I commit to running this relay at my current target through the next quarter.
            I won&apos;t move the goalposts until the next quarterly tax day opens on{" "}
            <b style={{ color: COLOR.ink }} data-testid="pact-next-window-date">
              {nextDateLabel}
            </b>
            {nextQuarter && (
              <span style={{ color: COLOR.muted }}> ({nextQuarter} quarterly tax day)</span>
            )}
            .
          </p>
          <p style={{ color: COLOR.muted, fontSize: "12px" }}>
            Between now and then, Payday Relay is here to help me log wins and refine my
            strategy in the Resource Library, but my headline target stays put. That&apos;s
            where the momentum comes from.
          </p>
        </div>

        <button
          onClick={onAgree}
          className="w-full px-5 py-4 rounded-full font-bold text-base transition-transform hover:scale-[1.02]"
          style={{ background: COLOR.gold, color: COLOR.ink }}
          data-testid="pact-agree-btn"
        >
          I&apos;m in. Launch my relay →
        </button>
        <button
          onClick={onCancel}
          className="w-full mt-3 text-xs underline"
          style={{ color: COLOR.muted }}
          data-testid="pact-cancel-btn"
        >
          Not quite ready, take me back
        </button>
      </div>
    </div>
  );
}



function SuccessView({ sheetUrl, token, isFourRelay, relayCount, onEdit }) {
  const hasFiredConfetti = useRef(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (hasFiredConfetti.current) return;
    hasFiredConfetti.current = true;
    // Payday Relay brand palette confetti burst, gold, red, indigo, turquoise
    const colors = [COLOR.gold, COLOR.red, COLOR.ink, COLOR.turquoise];
    const fire = (particleRatio, opts) =>
      confetti({
        origin: { y: 0.35 },
        colors,
        particleCount: Math.floor(220 * particleRatio),
        ...opts,
      });
    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.9 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  }, []);

  const shareText = isFourRelay
    ? "I just started my Free Sprint with Payday Relay, four weekly relays to a real income rhythm for my 1099 work. Check it out:"
    : "I just started my 52-relay season with Payday Relay, a whole year of income structure for my 1099 work. Check it out:";
  const shareUrl = "https://paydayrelay.com/";
  const shareFull = `${shareText} ${shareUrl}`;

  const handleShareNative = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "Payday Relay", text: shareText, url: shareUrl });
        toast.success("Thanks for spreading the word!");
      } else {
        await navigator.clipboard.writeText(shareFull);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
        toast.success("Copied, paste it anywhere!");
      }
    } catch {
      // User cancelled or clipboard blocked, silent
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareFull);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success("Copied!");
    } catch {
      toast.error("Copy didn't work, long-press the text to copy manually.");
    }
  };

  const smsHref = `sms:?&body=${encodeURIComponent(shareFull)}`;
  const emailHref = `mailto:?subject=${encodeURIComponent(
    "I just started Payday Relay",
  )}&body=${encodeURIComponent(shareFull)}`;

  return (
    <div data-testid="setup-success" className="pb-6">
      <div className="text-center pt-4 pb-8">
        <h1 className="text-3xl sm:text-4xl mb-3" style={{ ...serif, color: COLOR.ink }}>
          Your sheet is live in your Drive
        </h1>
        <p className="text-sm mb-8 leading-relaxed max-w-md mx-auto" style={{ color: COLOR.ink2 }}>
          Your Payday Relay sheet was copied into <b>your own Google Drive</b>. You own it. The
          LEGEND tab is pre-loaded with your Relay Calculator numbers, payday rhythm, and start date.
        </p>
        <a
          href={sheetUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-block px-8 py-4 rounded-full font-bold text-base transition-transform hover:scale-105"
          style={{ background: COLOR.gold, color: COLOR.ink }}
          data-testid="open-sheet-btn"
        >
          Open My Payday Relay Sheet →
        </a>
        <div className="mt-5">
          <Link
            to="/dashboard"
            className="inline-block px-8 py-4 rounded-full font-bold text-base border-2 transition-transform hover:scale-105"
            style={{ borderColor: COLOR.ink, color: COLOR.ink, background: "transparent" }}
            data-testid="setup-start-logging-btn"
          >
            Step 3 · Start logging your race →
          </Link>
        </div>
        <div className="mt-6 text-xs space-y-1" style={{ color: COLOR.muted }}>
          <p>Tip: bookmark it, or find it any time in Google Drive under "Payday Relay".</p>
          <button
            onClick={onEdit}
            className="underline"
            style={{ color: COLOR.ink2 }}
            data-testid="update-settings-btn"
          >
            Update my start date or payday rhythm
          </button>
          <p>
            Sheet stopped updating?{" "}
            <a
              href={`${API}/api/oauth/sheets/login?token=${encodeURIComponent(token || "")}`}
              className="underline"
              style={{ color: COLOR.ink2 }}
              data-testid="reconnect-google-link"
            >
              Reconnect Google
            </a>{" "}
. It re-approves access in a few seconds. Your sheet and numbers stay exactly as they are.
          </p>
        </div>
      </div>

      {/* Pass-the-baton share card */}
      <div
        className="rounded-2xl p-6 mb-8 text-center"
        style={{ background: "#FFF8E1", border: `1px solid ${"#EED89A"}` }}
        data-testid="share-card"
      >
        <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: COLOR.muted }}>
          Pass the baton
        </p>
        <h3 className="text-xl mb-2" style={{ ...serif, color: COLOR.ink }}>
          Know another 1099er who'd love this?
        </h3>
        <p className="text-sm mb-5 max-w-md mx-auto" style={{ color: COLOR.ink2 }}>
          Send them the Payday Relay Free Sprint, four relays free, and it opens with the same
          calculator that just built your rhythm.
        </p>
        <div className="flex flex-wrap gap-2 justify-center">
          <button
            onClick={handleShareNative}
            className="px-5 py-2.5 rounded-full font-semibold text-sm transition-transform hover:scale-105"
            style={{ background: COLOR.ink, color: "#fff" }}
            data-testid="share-native-btn"
          >
            Share
          </button>
          <a
            href={smsHref}
            className="px-5 py-2.5 rounded-full font-semibold text-sm border transition-transform hover:scale-105"
            style={{ borderColor: COLOR.ink, color: COLOR.ink, background: "#fff" }}
            data-testid="share-sms-btn"
          >
            Text a friend
          </a>
          <a
            href={emailHref}
            className="px-5 py-2.5 rounded-full font-semibold text-sm border transition-transform hover:scale-105"
            style={{ borderColor: COLOR.ink, color: COLOR.ink, background: "#fff" }}
            data-testid="share-email-btn"
          >
            Email it
          </a>
          <button
            onClick={handleCopy}
            className="px-5 py-2.5 rounded-full font-semibold text-sm border transition-transform hover:scale-105"
            style={{ borderColor: COLOR.ink, color: COLOR.ink, background: "#fff" }}
            data-testid="share-copy-btn"
          >
            {copied ? "Copied ✓" : "Copy link"}
          </button>
        </div>
      </div>
    </div>
  );
}

